import { Card } from 'react-bootstrap';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Carregando from '@/shared/components/Carregando';
import { FormSteps } from '@/shared/components/forms/FormSteps';
import Links from '@/shared/constants/navegacao';
import { CandidatoService } from '../../candidato.service';
import { podePromoverParaEstagiario } from '../../candidatoDetalhes.utils';
import { PromoverEstagiarioStepForm } from './PromoverEstagiarioStepForm';
import { CadastroEstagiarioWizardStep } from './CadastroEstagiarioWizardStep';
import { LotacaoEstagiarioWizardStep } from './LotacaoEstagiarioWizardStep';
import {
  PROMOVER_ESTAGIARIO_WIZARD_STEPS,
  type PromoverEstagiarioWizardState,
} from './promover-estagiario-wizard.models';
import { linkCandidatosProcessoSeletivo } from '../components/PromoverEstagiarioModal';
import { readPerson } from '@/modules/people/person.service';

const candidatoService = new CandidatoService();

export default function PromoverEstagiarioWizardPage() {
  const navigate = useNavigate();
  const params = useParams();
  const candidatoId = params.candidatoId ? Number(params.candidatoId) : 0;

  const [currentStep, setCurrentStep] = useState(1);
  const [wizardState, setWizardState] = useState<PromoverEstagiarioWizardState>({});
  const [loadingCandidato, setLoadingCandidato] = useState(true);
  const [resolvendoIdsLotacao, setResolvendoIdsLotacao] = useState(false);

  const voltarParaDetalhes = useCallback(() => {
    navigate(`${Links.Candidato_Detalhes}/${candidatoId}`);
  }, [candidatoId, navigate]);

  const voltarParaLista = useCallback(() => {
    const processoId = wizardState.processoSeletivoId;
    navigate(
      processoId != null
        ? linkCandidatosProcessoSeletivo(processoId)
        : Links.ProcessoSeletivo_Index,
    );
  }, [navigate, wizardState.processoSeletivoId]);

  const irParaEtapaAnterior = useCallback(() => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  }, []);

  useEffect(() => {
    if (candidatoId <= 0) {
      setLoadingCandidato(false);
      return;
    }

    let cancelled = false;
    setLoadingCandidato(true);

    candidatoService
      .findOne(candidatoId)
      .then((candidato) => {
        if (cancelled) return;

        setWizardState((prev) => ({
          ...prev,
          processoSeletivoId: candidato.processoSeletivoId,
          pessoaId: candidato.pessoa?.id,
          nomeEstagiario: candidato.pessoa?.nome,
        }));

        if (podePromoverParaEstagiario(candidato.situacao, candidato.pessoa)) {
          setCurrentStep(1);
        } else if (candidato.situacao === 'matriculado' && candidato.pessoa?.id) {
          setWizardState((prev) => ({
            ...prev,
            promocaoConcluida: true,
          }));
          setCurrentStep(2);
        } else {
          toast.warn('Este candidato n\u00e3o est\u00e1 apto para promo\u00e7\u00e3o.');
          voltarParaDetalhes();
        }
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e?.message || 'Falha ao carregar candidato.');
          voltarParaDetalhes();
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCandidato(false);
      });

    return () => {
      cancelled = true;
    };
  }, [candidatoId, voltarParaDetalhes]);

  const handlePromocaoSuccess = (data: {
    pessoaId: string;
    nome: string;
    promocaoDados: NonNullable<PromoverEstagiarioWizardState['promocaoDados']>;
  }) => {
    setWizardState((prev) => ({
      ...prev,
      pessoaId: data.pessoaId,
      nomeEstagiario: data.nome,
      promocaoConcluida: true,
      promocaoDados: data.promocaoDados,
    }));
    setCurrentStep(2);
  };

  const handleCadastroSuccess = (data: {
    estagiarioId: number;
    estagioId: number;
    nome: string;
  }) => {
    setWizardState((prev) => ({
      ...prev,
      estagiarioId: data.estagiarioId,
      estagioId: data.estagioId,
      nomeEstagiario: data.nome,
    }));
    setCurrentStep(3);
  };

  const handleStepClick = (stepId: number) => {
    if (stepId < currentStep) {
      setCurrentStep(stepId);
    }
  };

  useEffect(() => {
    if (currentStep !== 3) return;
    if (wizardState.estagiarioId && wizardState.estagioId) return;
    if (!wizardState.pessoaId) return;

    let cancelled = false;
    setResolvendoIdsLotacao(true);

    readPerson({
      skip: 0,
      take: 1,
      id: wizardState.pessoaId,
      comEstagiario: true,
      comEstagios: true,
    })
      .then((res) => {
        if (cancelled) return;
        const pessoa = res[0]?.[0];
        const estagiarioId = pessoa?.estagiario?.id;
        const estagios = pessoa?.estagiario?.estagios ?? [];
        const estagioAtivo = estagios.find((e) => !e.desligamento) ?? estagios[0];

        if (!estagiarioId || !estagioAtivo?.id) {
          toast.warn('Conclua o cadastro do estagi\u00e1rio antes de realizar a lota\u00e7\u00e3o.');
          setCurrentStep(2);
          return;
        }

        setWizardState((prev) => ({
          ...prev,
          estagiarioId,
          estagioId: estagioAtivo.id,
          nomeEstagiario: pessoa?.nome ?? prev.nomeEstagiario,
        }));
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : 'Falha ao carregar dados do estagi\u00e1rio.');
          setCurrentStep(2);
        }
      })
      .finally(() => {
        if (!cancelled) setResolvendoIdsLotacao(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentStep, wizardState.pessoaId, wizardState.estagiarioId, wizardState.estagioId]);

  if (loadingCandidato) {
    return <Carregando />;
  }

  return (
    <div>
      <div className="row mb-3">
        <div className="col-12">
          <h3>{'Promover candidato a estagi\u00e1rio'}</h3>
          <p className="text-muted small mb-0">
            {'Conclua a promo\u00e7\u00e3o, revise o cadastro e realize a lota\u00e7\u00e3o em um \u00fanico fluxo.'}
          </p>
        </div>
      </div>

      <FormSteps
        steps={PROMOVER_ESTAGIARIO_WIZARD_STEPS}
        currentStep={currentStep}
        onStepClick={handleStepClick}
        allowNavigateToCompleted
      />

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          {currentStep === 1 && (
            <PromoverEstagiarioStepForm
              candidatoId={candidatoId}
              promocaoConcluida={wizardState.promocaoConcluida}
              promocaoDados={wizardState.promocaoDados}
              pessoaId={wizardState.pessoaId}
              onSuccess={handlePromocaoSuccess}
              onCancel={voltarParaDetalhes}
            />
          )}

          {currentStep === 2 && wizardState.pessoaId && (
            <CadastroEstagiarioWizardStep
              pessoaId={wizardState.pessoaId}
              onBack={irParaEtapaAnterior}
              onCancel={voltarParaLista}
              onSuccess={handleCadastroSuccess}
            />
          )}

          {currentStep === 3 && (
            resolvendoIdsLotacao || !wizardState.estagiarioId || !wizardState.estagioId ? (
              <Carregando />
            ) : (
              <LotacaoEstagiarioWizardStep
                key={`${wizardState.estagiarioId}-${wizardState.estagioId}`}
                estagiarioId={wizardState.estagiarioId}
                estagioId={wizardState.estagioId}
                nomeEstagiario={wizardState.nomeEstagiario}
                onBack={irParaEtapaAnterior}
                onSuccess={voltarParaLista}
              />
            )
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
